import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { CrownOutlined } from "@ant-design/icons";

import { currentOfficeId, readCachedOfficeFeatures, useOfficeFeatures } from "../officeFeatures";
import { planGated } from "../sidebarAccess";
import MultiOfficeCheckout from "./MultiOfficeCheckout";
import "./checkout.css";

/**
 * The Choose-a-plan modal — ONE instance, mounted at the app root (App.js), opened
 * from anywhere with ``openPlanModal({reason, officeId})``: the left menu and the
 * Insurance Verification actions when the office has no plan, the trial banner,
 * a 402 from a metered request, and the Billing page.
 *
 * ``reason``: "trial_exhausted" | "no_plan" | "trial" | "billing".
 */
export const OPEN_PLAN_MODAL_EVENT = "open-plan-modal";

export function openPlanModal(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_PLAN_MODAL_EVENT, { detail }));
  } catch (e) {
    // Very old browsers: fall back to the Billing page, which has the same checkout.
    window.location.hash = "#/billing";
  }
}

/**
 * ``guard(action)`` runs ``action`` — or, when this office has no plan and no
 * trial left, opens the Choose-a-plan modal instead and returns false. For the
 * buttons that start paid work (New request, Resubmit, the request composer).
 */
export function usePlanGuard() {
  const status = useOfficeFeatures();
  return useCallback(
    (action) => {
      if (planGated(status)) {
        openPlanModal({ reason: status.plan && status.plan.reason });
        return false;
      }
      if (action) action();
      return true;
    },
    [status],
  );
}

/** The headline for why the modal opened. Pure, for tests. */
export function planModalCopy(reason, trial) {
  const quota = trial && trial.quota;
  switch (reason) {
    case "trial_exhausted":
      return {
        title: "You've used your free requests",
        sub: `You've used ${quota ? `all ${quota}` : "all"} of your free breakdown and estimate requests. ` +
          "Everything you've already fetched stays available — choose a plan to keep making requests.",
      };
    case "no_plan":
      return {
        title: "Choose a plan to keep using Smilepass",
        sub: "Your office doesn't have a plan yet. Pick one for each office and you're back to work in a minute.",
      };
    case "trial":
      return {
        title: "Plans",
        sub: trial ? `You've used ${trial.used} of your ${trial.quota} free requests. Choose a plan any time.` :
          "Choose a plan any time.",
      };
    default:
      return { title: "Choose a plan", sub: "Pick a plan for each office. Each office is charged separately." };
  }
}

export function PlanModalHost() {
  const [state, setState] = useState({ open: false, reason: null, officeId: null });

  useEffect(() => {
    const onOpen = (e) => {
      const detail = (e && e.detail) || {};
      const officeId = detail.officeId || currentOfficeId();
      if (!officeId) return;
      setState({ open: true, reason: detail.reason || null, officeId: String(officeId) });
    };
    window.addEventListener(OPEN_PLAN_MODAL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PLAN_MODAL_EVENT, onOpen);
  }, []);

  if (!state.open) return null;
  const cached = readCachedOfficeFeatures(state.officeId);
  const trial = cached && cached.plan ? cached.plan.trial : null;
  const copy = planModalCopy(state.reason, trial);
  const close = () => setState({ open: false, reason: null, officeId: null });

  return (
    <Modal
      open
      onCancel={close}
      footer={null}
      width={1040}
      destroyOnClose
      maskClosable
      zIndex={1100}
      className="cpm-modal"
    >
      <div className="cpm-head">
        <div className="cpm-icon"><CrownOutlined /></div>
        <div>
          <div className="cpm-title">{copy.title}</div>
          <div className="cpm-sub">{copy.sub}</div>
        </div>
      </div>
      <MultiOfficeCheckout
        officeId={state.officeId}
        onCancel={close}
        onDone={() => { close(); window.location.reload(); }}
      />
    </Modal>
  );
}

export default PlanModalHost;
